<template lang="pug">
.container-fluid.h-100(v-show="visible" data-cy="document-container")
  .row.px-2.py-4
    .col-12.main-document(v-if="hasDocument")
      h4.document-title
        | {{ t('components.navigation_index.main_document') }}
      h5.document-name
        a.document-name-link(
          :href="url_for_rails_documento_show(currentDocument.id)"
          :title="currentDocument.title"
        ) {{ currentDocument.title }}
      p.document-number
        | {{ currentDocument.numeroTOL }}

    .col-12.loading-overlay(v-if="isLoading")
      .spinner-border.text-primary(role="status")

    .col-12.vacatio-legis(v-if="hasFutureVacatios")
      h4.section-title
        | {{ t('components.navigation_index.vacatio_legis') }}
      ul.list-unstyled
        li(v-for="vacatio in currentVacatioLegis" :key="vacatio.id")
          a.vacatio-link(
            :href="url_for_rails_documento_show(vacatio.id)"
            :title="formatVacatioDate(vacatio.date)"
            data-cy="vacatio-link"
          ) {{ formatVacatioDate(vacatio.date) }}

    .col-12.redactions(v-if="hasRedactions")
      h4.section-title
        | {{ t('components.navigation_index.redactions') }}
      ul.list-unstyled
        li(v-for="redaction in sortedRedactions" :key="redaction.id")
          a.redaction-link(
            :href="buildRedactionUrl(redaction)"
            :title="redactionTitle(redaction)"
            data-cy="redaction-link"
          ) {{ redactionTitle(redaction) }}
</template>

<script>
import mixins from 'common/mixins'
import { mapState, mapGetters, mapActions } from 'vuex'
import { toDate } from 'common/string_utils'

export default {
  name: 'navigation-index-document-vue',
  props: {
    index: { type: String, default: '' },
    hideOnInit: { type: String, default: 'false' }
  },
  mixins: [mixins.translate, mixins.requests],
  computed: {
    currentVacatioLegis() {
      if (!Array.isArray(this.vacatioLegis)) return []

      const now = new Date()
      return this.vacatioLegis.filter(
        v => {
          if (!v || !v.date) return false

          const d = new Date(v.date)
          return !isNaN(d) && d > now
        }
      )
    },
    sortedRedactions() {
      if (!Array.isArray(this.redactions)) return []

      return [...this.redactions].sort((a, b) => {
        if (a.original && !b.original) return -1
        if (!a.original && b.original) return 1

        const aDate = toDate(a.fechadesde)
        const bDate = toDate(b.fechadesde)
        return bDate - aDate // newest first
      })
    },
    ...mapState('navigationIndex', ['currentDocument', 'vacatioLegis', 'redactions', 'visible']),
    ...mapGetters('navigationIndex', ['hasDocument', 'hasFutureVacatios', 'hasRedactions', 'isLoading'])
  },
  methods: {
    formatVacatioDate(dateStr) {
      if (!dateStr) return ''

      const d = new Date(dateStr)
      if (isNaN(d)) return dateStr

      const dd = String(d.getUTCDate()).padStart(2, '0')
      const mm = String(d.getUTCMonth() + 1).padStart(2, '0')
      const yyyy = d.getUTCFullYear()
      return `${dd}/${mm}/${yyyy}`
    },
    redactionTitle(redaction) {
      if (!redaction) return ''

      return redaction.original
        ? this.t('components.navigation_index.original_redaction')
        : redaction.title
    },
    buildRedactionUrl(redaction) {
      if (!redaction) return ''

      return this.url_for_rails_documento_show(
        this.currentDocument.id,
        { redaccion: redaction.id }
      )
    },
    ...mapActions('navigationIndex', ['clearNavigationDocument', 'setNavigationDocument'])
  },
  mounted() {
    this.clearNavigationDocument()
    if (this.hideOnInit === 'true')
      this.setNavigationDocument({})
  }
}
</script>

<style scoped>
.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
}

.document-title {
  color: #666;
  font-size: 0.9rem;
  font-weight: bold;
  margin-bottom: 0.5rem;
  text-transform: uppercase;
}

.document-name {
  color: #333;
  font-size: 1.1rem;
  font-weight: bold;
  margin-bottom: 0.25rem;
  line-height: 1.3;
}

.document-number {
  color: #666;
  font-size: 0.9rem;
  margin-bottom: 1.5rem;
}

.section-title {
  color: #666;
  font-size: 0.9rem;
  font-weight: bold;
  margin-bottom: 0.5rem;
  margin-top: 1.5rem;
  text-transform: uppercase;
}

ul.list-unstyled li {
  margin-bottom: 0.5rem;
  color: #333;
}

.vacatio-link {
  text-decoration: underline;
  cursor: pointer;
}

</style>
